import { supabase } from './supabase'

// Reusing existing Issues schema for user curations
// User curations are just issues where curator_id = logged in user
// Admin issues are created from /admin and can have any curator_id set

export type CurationLayout = 'grid' | 'masonry'
export type CurationCornerStyle = 'rounded' | 'square'

export interface Curation {
  id: string
  curator_id: string
  title: string
  slug: string
  narrative?: string // Description field in issues table
  layout_type: CurationLayout
  rounded_corners?: boolean // Corner style for grid items
  highlighted_image_url?: string
  status: string
  is_default?: boolean // True for user's "Saved Items" curation
  published_at?: string
  created_at: string
  updated_at: string
  item_count?: number
}

export interface CurationItem {
  id: string
  issue_id: string
  contribution_id: string
  position: number
  custom_description?: string // Using as "note"
  created_at: string
  updated_at?: string
}

export interface CurationWithItems extends Curation {
  items: CurationItem[]
}

export interface CurationWithCurator extends Curation {
  curator: {
    id: string
    username: string
    first_name?: string
    last_name?: string
    avatar_url?: string
    // Guest curator fields — drive the editorial hero on guest curations
    is_guest?: boolean
    editorial_profile?: boolean
    curator_kicker?: string | null
    role_headline?: string | null
    bio?: string | null
    instagram_handle?: string | null
    website?: string | null
  }
}

export interface CreateCurationInput {
  title: string
  narrative?: string
  highlighted_image_url?: string
  layout_type?: CurationLayout
  rounded_corners?: boolean
  status?: 'private' | 'public'
}

export interface UpdateCurationInput {
  title?: string
  narrative?: string
  highlighted_image_url?: string
  layout_type?: CurationLayout
  rounded_corners?: boolean
}

export interface CreateCurationItemInput {
  contribution_id: string
  custom_description?: string // Note
  position: number
}

export interface UpdateCurationItemInput {
  custom_description?: string
  position?: number
}

// Constants
export const MAX_CURATIONS_PER_USER = 100
export const MAX_ITEMS_PER_CURATION = 200
export const MIN_TITLE_LENGTH = 3
export const MAX_TITLE_LENGTH = 255
export const MAX_DESCRIPTION_LENGTH = 1500
export const MAX_NOTE_LENGTH = 280

/**
 * Generate a URL-safe slug from a string
 */
export function generateSlug(title: string): string {
  return title
    .toLowerCase()
    .trim()
    .replace(/[^\w\s-]/g, '') // Remove non-word chars except spaces and hyphens
    .replace(/[\s_]+/g, '-') // Replace spaces and underscores with hyphens
    .replace(/^-+|-+$/g, '') // Remove leading/trailing hyphens
}

/**
 * Validate curation title
 */
export function validateTitle(title: string): { valid: boolean; error?: string } {
  const trimmed = title.trim()
  if (trimmed.length < MIN_TITLE_LENGTH) {
    return { valid: false, error: `Title must be at least ${MIN_TITLE_LENGTH} characters` }
  }
  if (trimmed.length > MAX_TITLE_LENGTH) {
    return { valid: false, error: `Title must be no more than ${MAX_TITLE_LENGTH} characters` }
  }
  return { valid: true }
}

/**
 * Validate curation narrative (description)
 */
export function validateNarrative(narrative?: string): { valid: boolean; error?: string } {
  if (!narrative) return { valid: true }
  if (narrative.length > MAX_DESCRIPTION_LENGTH) {
    return { valid: false, error: `Description must be no more than ${MAX_DESCRIPTION_LENGTH} characters` }
  }
  return { valid: true }
}

// Backwards compatibility alias
export const validateDescription = validateNarrative

export const curationsService = {
  /**
   * Get all curations by a user (both published and private)
   * Private curations appear on user's profile, published ones also appear on curated feed
   */
  async getUserCurations(userId: string, includeDefault = false): Promise<Curation[]> {
    let query = supabase
      .from('issues')
      .select('*')
      .eq('curator_id', userId)
      .in('status', ['published', 'public', 'private'])
      .order('updated_at', { ascending: false })

    if (!includeDefault) {
      query = query.eq('is_default', false)
    }

    const { data, error } = await query
    if (error) throw error
    return data || []
  },

  /**
   * Same as getUserCurations, but includes each curation's item_count via a
   * single embedded `issue_items(count)` aggregate — instead of the caller
   * fetching every item row of every curation just to count them (the profile
   * page's old N+1). One query for the whole list.
   */
  async getUserCurationsWithCounts(userId: string, includeDefault = false): Promise<Curation[]> {
    let query = supabase
      .from('issues')
      .select('*, issue_items(count)')
      .eq('curator_id', userId)
      .in('status', ['published', 'public', 'private'])
      .order('updated_at', { ascending: false })

    if (!includeDefault) {
      query = query.eq('is_default', false)
    }

    const { data, error } = await query
    if (error) throw error
    return (data || []).map((c: any) => ({
      ...c,
      item_count: c.issue_items?.[0]?.count ?? 0,
    }))
  },

  /**
   * Get or create the user's default "Saved Items" curation.
   * Uses upsert-style: tries to fetch first, creates if not found.
   * Partial unique index prevents duplicates even under race conditions.
   */
  async getOrCreateDefaultCuration(userId: string): Promise<Curation> {
    // Try to fetch existing default curation
    const { data: existing } = await supabase
      .from('issues')
      .select('*')
      .eq('curator_id', userId)
      .eq('is_default', true)
      .single()

    if (existing) return existing

    // Create default curation
    const slug = 'saved-items-' + userId.substring(0, 8)
    const { data, error } = await supabase
      .from('issues')
      .insert({
        curator_id: userId,
        created_by: userId,
        title: 'Saved Items',
        slug,
        status: 'private',
        is_default: true,
        layout_type: 'grid'
      })
      .select()
      .single()

    if (error) {
      // Race condition: another request created it first (23505 = unique violation)
      if (error.code === '23505') {
        const { data: retry } = await supabase
          .from('issues')
          .select('*')
          .eq('curator_id', userId)
          .eq('is_default', true)
          .single()
        if (retry) return retry
      }
      throw error
    }

    return data
  },

  /**
   * Get the user's default curation (returns null if not yet created)
   */
  async getUserDefaultCuration(userId: string): Promise<Curation | null> {
    const { data, error } = await supabase
      .from('issues')
      .select('*')
      .eq('curator_id', userId)
      .eq('is_default', true)
      .single()

    if (error && error.code !== 'PGRST116') throw error
    return data || null
  },

  /**
   * Toggle save: add/remove item from user's default curation.
   * Direct insert/delete on issue_items — bypasses addCurationItem's 200-item limit.
   * Returns true if saved, false if unsaved.
   */
  async toggleSave(userId: string, contributionId: string): Promise<boolean> {
    const defaultCuration = await this.getOrCreateDefaultCuration(userId)

    // Check if already saved
    const { data: existing } = await supabase
      .from('issue_items')
      .select('id')
      .eq('issue_id', defaultCuration.id)
      .eq('contribution_id', contributionId)
      .single()

    if (existing) {
      // Unsave
      const { error } = await supabase
        .from('issue_items')
        .delete()
        .eq('id', existing.id)

      if (error) throw error
      return false
    } else {
      // Save — get next position
      const { data: lastItem } = await supabase
        .from('issue_items')
        .select('position')
        .eq('issue_id', defaultCuration.id)
        .order('position', { ascending: false })
        .limit(1)
        .single()

      const nextPosition = lastItem ? lastItem.position + 1 : 0

      const { error } = await supabase
        .from('issue_items')
        .insert({
          issue_id: defaultCuration.id,
          contribution_id: contributionId,
          position: nextPosition
        })

      if (error) {
        // Already saved (race condition)
        if (error.code === '23505') return true
        throw error
      }
      return true
    }
  },

  /**
   * Batch check which contributions are saved by a user (in their default curation).
   * Returns a Set of contribution IDs that are saved.
   */
  async checkUserSaves(userId: string, contributionIds: string[]): Promise<Set<string>> {
    if (contributionIds.length === 0) return new Set()

    // Get user's default curation
    const defaultCuration = await this.getUserDefaultCuration(userId)
    if (!defaultCuration) return new Set()

    const { data, error } = await supabase
      .from('issue_items')
      .select('contribution_id')
      .eq('issue_id', defaultCuration.id)
      .in('contribution_id', contributionIds)

    if (error) throw error

    return new Set(data?.map(row => row.contribution_id) || [])
  },

  /**
   * Get contributions saved by a user (from their default curation).
   */
  async getUserSavedContributions(userId: string): Promise<any[]> {
    const defaultCuration = await this.getUserDefaultCuration(userId)
    if (!defaultCuration) return []

    const { data, error } = await supabase
      .from('issue_items')
      .select(`
        contribution_id,
        contributions!inner (
          *,
          user:users!contributions_user_id_fkey (
            id,
            email,
            first_name,
            last_name,
            username,
            avatar_url
          )
        )
      `)
      .eq('issue_id', defaultCuration.id)
      .order('created_at', { ascending: false })

    if (error) throw error
    if (!data) return []

    return data.map(item => (item as any).contributions)
  },

  /**
   * Get a specific curation by owner username and slug.
   * Returns private / public / published; RLS decides visibility per viewer
   * (owner + admins see all; visitors see public + published — public curations
   * are genuinely viewable via direct link, private ones only by the owner).
   */
  async getCurationByUsernameAndSlug(username: string, slug: string, knownCurator?: any): Promise<CurationWithCurator | null> {
    // Resolve the curator. Callers that already hold it (the hub knows its
    // guest) pass it in so we skip the redundant users-by-username lookup.
    let curator = knownCurator?.id ? knownCurator : null
    if (!curator) {
      const { data: userData, error: userError } = await supabase
        .from('users')
        .select('id, username, first_name, last_name, avatar_url, is_guest, editorial_profile, curator_kicker, role_headline, bio, instagram_handle, website')
        .eq('username', username)
        .single()
      if (userError || !userData) return null
      curator = userData
    }

    // Then get the curation (both published and private are viewable)
    const { data, error } = await supabase
      .from('issues')
      .select('*')
      .eq('curator_id', curator.id)
      .eq('slug', slug)
      .in('status', ['published', 'public', 'private'])
      .single()

    if (error || !data) return null

    return {
      ...data,
      curator
    }
  },

  /**
   * Load a curation's items in the display shape CurationDetail renders
   * (per-item save state + saves/comments counts). Single source of truth so
   * the detail view and the hub's neighbour-prefetch stay in lockstep.
   */
  async getCurationItemsForDisplay(curationId: string, viewer?: { id: string } | null): Promise<any[]> {
    const { data: itemsData, error: itemsError } = await supabase
      .from('issue_items')
      .select(`
        id,
        position,
        custom_description,
        contributions!inner(
          id,
          title,
          source_url,
          cached_image_url,
          images,
          created_at,
          user_id,
          brands(
            name
          ),
          users!contributions_user_id_fkey(
            id,
            username,
            first_name,
            last_name,
            avatar_url
          )
        )
      `)
      .eq('issue_id', curationId)
      .order('position', { ascending: true })

    if (itemsError) throw itemsError

    // Per-viewer save state + comment counts (only when signed in)
    let likesData: any[] = []
    let commentsData: any[] = []
    if (viewer) {
      const contributionIds = (itemsData || []).map((item: any) => item.contributions.id)
      const savedSet = await this.checkUserSaves(viewer.id, contributionIds)
      likesData = contributionIds.filter((id: string) => savedSet.has(id)).map((id: string) => ({ contribution_id: id }))

      const { data: comments } = await supabase
        .from('comments')
        .select('contribution_id')
        .in('contribution_id', contributionIds)
      commentsData = comments || []
    }

    // Saves counts straight from the contributions rows
    const savesCounts: { [key: string]: number } = {}
    ;(itemsData || []).forEach((item: any) => {
      if (item.contributions) {
        savesCounts[item.contributions.id] = item.contributions.saves_count || 0
      }
    })

    // Comment counts per contribution
    const commentsCounts: { [key: string]: number } = {}
    ;(commentsData || []).forEach((comment: any) => {
      commentsCounts[comment.contribution_id] = (commentsCounts[comment.contribution_id] || 0) + 1
    })

    return (itemsData || []).map((item: any) => {
      const contribution = item.contributions
      if (!contribution) return null

      const highlightedImage = contribution.images?.find((img: any) => img.highlighted)
      const firstImage = contribution.images?.[0]
      const displayImage =
        highlightedImage?.cached_url ||
        firstImage?.cached_url ||
        contribution.cached_image_url ||
        highlightedImage?.url ||
        firstImage?.url

      const isSaved = likesData.some((like: any) => like.contribution_id === contribution.id)

      return {
        id: contribution.id,
        item_id: item.id,
        contribution_id: contribution.id,
        title: contribution.title,
        brand: contribution.brands?.name,
        brand_name: contribution.brands?.name,
        cached_image: displayImage,
        cached_image_url: displayImage,
        source_url: contribution.source_url,
        product_url: contribution.source_url, // For ItemCard
        custom_description: item.custom_description,
        position: item.position,
        images: contribution.images,
        created_at: contribution.created_at,
        dateAdded: contribution.created_at,
        isSaved: isSaved,
        saves_count: savesCounts[contribution.id] || 0,
        comments_count: commentsCounts[contribution.id] || 0,
        user: contribution.users ? {
          id: contribution.users.id,
          username: contribution.users.username,
          first_name: contribution.users.first_name,
          last_name: contribution.users.last_name,
          avatar_url: contribution.users.avatar_url
        } : null
      }
    }).filter((item: any) => item !== null)
  },

  /**
   * Likes / comments counts + whether the viewer liked this curation.
   */
  async getCurationInteractions(curationId: string, viewer?: { id: string } | null): Promise<{ likesCount: number; commentsCount: number; isLiked: boolean }> {
    try {
      const [likesCountData, commentsCountData, hasLiked] = await Promise.all([
        this.getCurationLikesCount(curationId),
        this.getCurationCommentsCount(curationId),
        viewer ? this.hasUserLikedCuration(curationId, viewer.id) : Promise.resolve(false),
      ])
      return { likesCount: likesCountData, commentsCount: commentsCountData, isLiked: !!hasLiked }
    } catch (err) {
      console.error('Error loading curation interactions:', err)
      return { likesCount: 0, commentsCount: 0, isLiked: false }
    }
  },

  /**
   * Everything CurationDetail needs for one curation: summary + curator, items
   * (display shape), and interaction counts. Used by the detail view's loader
   * and by the hub to prefetch adjacent curations into the query cache.
   */
  async getCurationBundle(username: string, slug: string, viewer?: { id: string } | null, knownCurator?: any): Promise<{ curation: CurationWithCurator; items: any[]; likesCount: number; commentsCount: number; isLiked: boolean } | null> {
    const curation = await this.getCurationByUsernameAndSlug(username, slug, knownCurator)
    if (!curation) return null
    const [items, interactions] = await Promise.all([
      this.getCurationItemsForDisplay(curation.id, viewer),
      this.getCurationInteractions(curation.id, viewer),
    ])
    return { curation, items, ...interactions }
  },

  /**
   * Get a curation by ID
   */
  async getCuration(curationId: string): Promise<Curation | null> {
    const { data, error } = await supabase
      .from('issues')
      .select('*')
      .eq('id', curationId)
      .single()

    if (error) return null
    return data
  },

  /**
   * Get a curation with its items
   */
  async getCurationWithItems(curationId: string): Promise<CurationWithItems | null> {
    const { data: curation, error: curationError } = await supabase
      .from('issues')
      .select('*')
      .eq('id', curationId)
      .single()

    if (curationError || !curation) return null

    const { data: items, error: itemsError } = await supabase
      .from('issue_items')
      .select('*')
      .eq('issue_id', curationId)
      .order('position', { ascending: true })

    if (itemsError) throw itemsError

    return {
      ...curation,
      items: items || []
    }
  },

  /**
   * Create a new curation
   */
  async createCuration(userId: string, input: CreateCurationInput): Promise<Curation> {
    // Validate title
    const titleValidation = validateTitle(input.title)
    if (!titleValidation.valid) {
      throw new Error(titleValidation.error)
    }

    // Validate narrative
    const narrativeValidation = validateNarrative(input.narrative)
    if (!narrativeValidation.valid) {
      throw new Error(narrativeValidation.error)
    }

    // Check user hasn't exceeded curation limit
    const { data: existingCurations } = await supabase
      .from('issues')
      .select('id')
      .eq('curator_id', userId)

    if (existingCurations && existingCurations.length >= MAX_CURATIONS_PER_USER) {
      throw new Error(`You can only create up to ${MAX_CURATIONS_PER_USER} curations`)
    }

    // Generate slug
    const baseSlug = generateSlug(input.title)

    // Check for slug uniqueness for this user
    let finalSlug = baseSlug
    let counter = 2
    while (true) {
      const { data: existing } = await supabase
        .from('issues')
        .select('id')
        .eq('curator_id', userId)
        .eq('slug', finalSlug)
        .single()

      if (!existing) break
      finalSlug = `${baseSlug}-${counter}`
      counter++
    }

    // Create curation - defaults to public (owner-visible + shareable, not in
    // the feed); only admins can publish to the feed.
    // Must set created_by for RLS policy (issues_insert_policy checks created_by = auth.uid())
    const { data, error } = await supabase
      .from('issues')
      .insert({
        curator_id: userId,
        created_by: userId,
        title: input.title.trim(),
        slug: finalSlug,
        narrative: input.narrative?.trim(),
        highlighted_image_url: input.highlighted_image_url,
        layout_type: input.layout_type || 'masonry',
        rounded_corners: input.rounded_corners !== undefined ? input.rounded_corners : false,
        // Curations default to public (owner-visible + shareable, not in the
        // feed); the create/edit modal's "Make private" toggle opts into private.
        status: input.status || 'public'
      })
      .select()
      .single()

    if (error) throw error
    return data
  },

  /**
   * Update a curation
   */
  async updateCuration(curationId: string, input: UpdateCurationInput): Promise<Curation> {
    const updates: any = {}

    if (input.title !== undefined) {
      const titleValidation = validateTitle(input.title)
      if (!titleValidation.valid) {
        throw new Error(titleValidation.error)
      }
      updates.title = input.title.trim()
    }

    if (input.narrative !== undefined) {
      const narrativeValidation = validateNarrative(input.narrative)
      if (!narrativeValidation.valid) {
        throw new Error(narrativeValidation.error)
      }
      updates.narrative = input.narrative.trim()
    }

    if (input.highlighted_image_url !== undefined) {
      updates.highlighted_image_url = input.highlighted_image_url
    }

    if (input.layout_type !== undefined) {
      updates.layout_type = input.layout_type
    }

    if (input.rounded_corners !== undefined) {
      updates.rounded_corners = input.rounded_corners
    }

    const { data, error } = await supabase
      .from('issues')
      .update(updates)
      .eq('id', curationId)
      .select()
      .single()

    if (error) throw error
    return data
  },

  /**
   * Set a curation's visibility. Owner-facing: only 'private' or 'public' — the
   * DB publish-guard trigger (enforce_curation_status_transitions) is the real
   * gate on 'published', so this never sends it. RLS + trigger enforce
   * authorization; callers do the optimistic update / revert / toast.
   */
  async setCurationStatus(curationId: string, status: 'private' | 'public'): Promise<void> {
    // Defense in depth against a caller casting past the type: publishing is
    // admin-only, enforced by the DB trigger. This service never sends it.
    if (status !== 'private' && status !== 'public') {
      throw new Error("setCurationStatus only sets 'private' or 'public'; publishing is admin-only")
    }
    const { error } = await supabase
      .from('issues')
      .update({ status })
      .eq('id', curationId)

    if (error) throw error
  },

  /**
   * Delete a curation
   */
  async deleteCuration(curationId: string): Promise<void> {
    const { error } = await supabase
      .from('issues')
      .delete()
      .eq('id', curationId)

    if (error) throw error
  },

  /**
   * Get items in a curation
   */
  async getCurationItems(curationId: string): Promise<CurationItem[]> {
    const { data, error } = await supabase
      .from('issue_items')
      .select('*')
      .eq('issue_id', curationId)
      .order('position', { ascending: true })

    if (error) throw error
    return data || []
  },

  /**
   * Add an item to a curation
   */
  async addCurationItem(curationId: string, input: CreateCurationItemInput): Promise<CurationItem> {
    // Check item count limit
    const { data: existingItems } = await supabase
      .from('issue_items')
      .select('id')
      .eq('issue_id', curationId)

    if (existingItems && existingItems.length >= MAX_ITEMS_PER_CURATION) {
      throw new Error(`Curations can only contain up to ${MAX_ITEMS_PER_CURATION} items`)
    }

    const { data, error } = await supabase
      .from('issue_items')
      .insert({
        issue_id: curationId,
        contribution_id: input.contribution_id,
        custom_description: input.custom_description?.trim(),
        position: input.position
      })
      .select()
      .single()

    if (error) throw error
    return data
  },

  /**
   * Update a curation item
   */
  async updateCurationItem(itemId: string, input: UpdateCurationItemInput): Promise<CurationItem> {
    const updates: any = {}

    if (input.custom_description !== undefined) {
      if (input.custom_description.length > MAX_NOTE_LENGTH) {
        throw new Error(`Notes must be no more than ${MAX_NOTE_LENGTH} characters`)
      }
      updates.custom_description = input.custom_description.trim()
    }

    if (input.position !== undefined) {
      updates.position = input.position
    }

    const { data, error } = await supabase
      .from('issue_items')
      .update(updates)
      .eq('id', itemId)
      .select()
      .single()

    if (error) throw error
    return data
  },

  /**
   * Remove an item from a curation
   */
  async removeCurationItem(itemId: string): Promise<void> {
    const { error } = await supabase
      .from('issue_items')
      .delete()
      .eq('id', itemId)

    if (error) throw error
  },

  /**
   * Reorder items in a curation
   */
  async reorderCurationItems(items: Array<{ id: string; position: number }>): Promise<void> {
    if (items.length === 0) return

    // Use a large offset (1 million + random) to guarantee unique temporary positions
    // This ensures no collision with final positions (0, 1, 2...) or existing positions
    const tempOffset = 1000000 + Math.floor(Math.random() * 1000000)

    // Step 1: Move all items to temporary positions (tempOffset + index)
    // These positions are guaranteed to be unique and far from final positions
    for (let i = 0; i < items.length; i++) {
      const { error } = await supabase
        .from('issue_items')
        .update({ position: tempOffset + i })
        .eq('id', items[i].id)

      if (error) {
        console.error('Reorder temp error:', error)
        throw new Error(`Failed to reorder items: ${error.message}`)
      }
    }

    // Step 2: Update to final positions (0, 1, 2, ...)
    // Safe because all items are now at positions > tempOffset
    for (const item of items) {
      const { error } = await supabase
        .from('issue_items')
        .update({ position: item.position })
        .eq('id', item.id)

      if (error) {
        console.error('Reorder final error:', error)
        throw new Error(`Failed to reorder items: ${error.message}`)
      }
    }
  },

  /**
   * Update the curation's cover image to match the first item (position 0)
   */
  async updateCoverImageToFirstItem(curationId: string): Promise<void> {
    // Get the first item (position 0) with its contribution's image
    const { data: firstItem, error: itemError } = await supabase
      .from('issue_items')
      .select(`
        contribution_id,
        contributions!inner(
          cached_image_url,
          images
        )
      `)
      .eq('issue_id', curationId)
      .order('position', { ascending: true })
      .limit(1)
      .single()

    if (itemError || !firstItem) {
      // No items in curation, clear the cover image
      await supabase
        .from('issues')
        .update({ highlighted_image_url: null })
        .eq('id', curationId)
      return
    }

    const contribution = firstItem.contributions as any
    let coverImageUrl = contribution.cached_image_url

    // Check for highlighted image in images array
    if (contribution.images && contribution.images.length > 0) {
      const highlightedImage = contribution.images.find((img: any) => img.highlighted)
      if (highlightedImage) {
        coverImageUrl = highlightedImage.cached_url || highlightedImage.url
      }
    }

    // Update the curation's cover image
    const { error: updateError } = await supabase
      .from('issues')
      .update({ highlighted_image_url: coverImageUrl })
      .eq('id', curationId)

    if (updateError) throw updateError
  },

  /**
   * Toggle save on a curation (bookmarks a curation via issue_likes table)
   */
  async toggleCurationSave(curationId: string, userId: string): Promise<boolean> {
    // Check if already liked
    const { data: existingLike } = await supabase
      .from('issue_likes')
      .select('id')
      .eq('issue_id', curationId)
      .eq('user_id', userId)
      .maybeSingle()

    if (existingLike) {
      // Unlike
      const { error } = await supabase
        .from('issue_likes')
        .delete()
        .eq('issue_id', curationId)
        .eq('user_id', userId)

      if (error) throw error
      return false
    } else {
      // Like
      const { error } = await supabase
        .from('issue_likes')
        .insert({ issue_id: curationId, user_id: userId })

      if (error) throw error
      return true
    }
  },

  /**
   * Get likes count for a curation
   */
  async getCurationLikesCount(curationId: string): Promise<number> {
    const { count, error } = await supabase
      .from('issue_likes')
      .select('*', { count: 'exact', head: true })
      .eq('issue_id', curationId)

    if (error) throw error
    return count || 0
  },

  /**
   * Check if user has liked a curation
   */
  async hasUserLikedCuration(curationId: string, userId: string): Promise<boolean> {
    const { data, error } = await supabase
      .from('issue_likes')
      .select('id')
      .eq('issue_id', curationId)
      .eq('user_id', userId)
      .maybeSingle()

    if (error) throw error
    return !!data
  },

  /**
   * Get users who liked a curation
   */
  async getCurationLikes(curationId: string): Promise<any[]> {
    const { data, error } = await supabase
      .from('issue_likes')
      .select(`
        id,
        created_at,
        user:users!user_id(
          id,
          username,
          first_name,
          last_name,
          avatar_url
        )
      `)
      .eq('issue_id', curationId)
      .order('created_at', { ascending: false })

    if (error) throw error
    return data || []
  },

  /**
   * Get comments for a curation
   */
  async getCurationComments(curationId: string): Promise<any[]> {
    const { data, error } = await supabase
      .from('issue_comments')
      .select(`
        id,
        content,
        created_at,
        updated_at,
        user_id,
        attachment_type,
        attachment_id,
        user:users!user_id(
          id,
          username,
          first_name,
          last_name,
          avatar_url
        )
      `)
      .eq('issue_id', curationId)
      .order('created_at', { ascending: true })

    if (error) throw error
    return data || []
  },

  /**
   * Get comments count for a curation
   */
  async getCurationCommentsCount(curationId: string): Promise<number> {
    const { count, error } = await supabase
      .from('issue_comments')
      .select('*', { count: 'exact', head: true })
      .eq('issue_id', curationId)

    if (error) throw error
    return count || 0
  },

  /**
   * Add a comment to a curation
   */
  async addCurationComment(
    curationId: string,
    userId: string,
    content: string,
    attachment?: { type: string; id: string } | null
  ): Promise<any> {
    const trimmedContent = content.trim()

    if (trimmedContent.length === 0) {
      throw new Error('Comment cannot be empty')
    }

    if (trimmedContent.length > 2000) {
      throw new Error('Comment must be no more than 2000 characters')
    }

    const row: any = {
      issue_id: curationId,
      user_id: userId,
      content: trimmedContent
    }
    if (attachment) {
      row.attachment_type = attachment.type
      row.attachment_id = attachment.id
    }

    const { data, error } = await supabase
      .from('issue_comments')
      .insert(row)
      .select(`
        id,
        content,
        created_at,
        updated_at,
        user_id,
        attachment_type,
        attachment_id,
        user:users!user_id(
          id,
          username,
          first_name,
          last_name,
          avatar_url
        )
      `)
      .single()

    if (error) throw error
    return data
  },

  /**
   * Update a curation comment
   */
  async updateCurationComment(commentId: string, content: string): Promise<any> {
    const trimmedContent = content.trim()

    if (trimmedContent.length === 0) {
      throw new Error('Comment cannot be empty')
    }

    if (trimmedContent.length > 2000) {
      throw new Error('Comment must be no more than 2000 characters')
    }

    const { data, error } = await supabase
      .from('issue_comments')
      .update({
        content: trimmedContent,
        updated_at: new Date().toISOString()
      })
      .eq('id', commentId)
      .select(`
        id,
        content,
        created_at,
        updated_at,
        user:users!user_id(
          id,
          username,
          first_name,
          last_name,
          avatar_url
        )
      `)
      .single()

    if (error) throw error
    return data
  },

  /**
   * Delete a curation comment
   */
  async deleteCurationComment(commentId: string): Promise<void> {
    const { error } = await supabase
      .from('issue_comments')
      .delete()
      .eq('id', commentId)

    if (error) throw error
  },

  /**
   * Get curations saved (bookmarked) by a user
   */
  async getUserSavedCurations(userId: string): Promise<Curation[]> {
    const { data, error } = await supabase
      .from('issue_likes')
      .select(`
        issue_id,
        issues!inner (
          *,
          curator:users!issues_curator_id_fkey (
            id,
            username,
            first_name,
            last_name,
            avatar_url
          )
        )
      `)
      .eq('user_id', userId)
      .order('created_at', { ascending: false })

    if (error) throw error

    // Extract the issues from the nested structure
    if (!data) return []

    return data.map(item => (item as any).issues) as Curation[]
  }
}
